import { Language } from '../../context/LanguageContext';
import { formatTextWithParams } from '../../utils/formatTextWithParams';
import CircularProgress from '../progress-bars/circularProgress/CircularProgress';
import './Task.css'
import { useEffect, useState } from 'react';
import { translations } from '../../configs/translations';
import { animNumber } from '../../utils/animNumber';

export interface TaskProps {
    title?: string;
    description?: string;
    type: 'battlepass-level-card' | 'big' | 'just-text' | 'daily';
    lvl?: number;
    lvlName?: string;
    exp?: number;
    maxExp?: number;
    icon?: string;
    prize?: number;
}

export const Task = ({title, description, type, lvl, lvlName, exp, maxExp, icon, prize}: TaskProps) => {
    switch (type) {
        case 'battlepass-level-card':
            return <BattlePassLevelCard 
                lvl={lvl || 0}
                lvlName={lvlName || 'UNKNOWN'}
                exp={exp || 0}
                maxExp={maxExp || 0}
            />;
        case 'big':
            return <TaskBig title={title || ''} description={description || ''} value={exp || 0} outOf={maxExp || 100} />;
        case 'just-text':
            return <TaskJustText title={title || ''} description={description || ''} />;
        case 'daily':
            return <TaskDaily 
                title={title || ''} 
                description={description || ''} 
                value={exp || 0} 
                outOf={maxExp || 100} 
                icon={icon || ''}
                prize={prize || 0}
            />;
    }
}

interface BattlePassLevelCardProps {
    lvl: number;
    lvlName: string;
    exp: number;
    maxExp: number;
}

export const BattlePassLevelCard = ({lvl, lvlName, exp, maxExp}: BattlePassLevelCardProps) => {
    const [language, setLanguage] = useState<Language>('ru');
    const t = translations[language];
    
    return (
        <div className="task">
            <div className="battlepass-level-card-wrapper">
                <CircularProgress 
                    valueNum={151} 
                    unit="M" 
                    percent={getPercent(exp, maxExp)} 
                    styles={{
                        filter: 'drop-shadow(0px 0px 20px #FFA61670)',
                    }}
                />
                <div className="battlepass-level-card-content-wrapper">
                    <div className="battlepass-level-card">
                        <p>{formatTextWithParams(t.bp_exp_until_level, [lvl])}</p>
                        <p><span style={{ color: '#fff' }}>{exp}</span> / {maxExp}</p>
                    </div>
                    <div className="battle-pass-level-label">
                        <p>{lvlName}</p>
                    </div>
                </div>
            </div>
        </div>
    )
}

interface TaskBigProps {
    title: string;
    description?: string;
    value?: number;
    outOf?: number;
}

const getPercent = (value: number, outOf: number) => {
    return (value / outOf) * 100;
}

export const TaskBig = ({title, description, value, outOf}: TaskBigProps) => {
    const [language, setLanguage] = useState<Language>('ru');
    const t = translations[language];

    return (
        <div className="task big">
            <CircularProgress secondValueNum={outOf || 100} valueNum={value || 0} percent={getPercent(value || 0, outOf || 100)}/>
            <div className='task-big-content'>
                <p className="task-title big">{title}</p>
                {description && <p className="task-description big">{description}</p>}
            </div>
            <div className="task-change-button-container">
                <div 
                    onClick={() => console.log('buy')} 
                    className="task-change-button"
                >
                    <p>{t.change_task}</p>
                </div>
            </div>
        </div>
    )
}

interface TaskDailyProps {
    title: string;
    description?: string;
    value?: number;
    outOf?: number;
    icon?: string;
    prize?: number;
}

export const TaskDaily = ({title, description, value, outOf, icon, prize}: TaskDailyProps) => {
    const [language, setLanguage] = useState<Language>('ru');
    const t = translations[language];

    const [progress, setProgress] = useState<number | null>(null);

    const getTitle = () => {
        return formatTextWithParams(t['daily_' + title as keyof typeof t], [outOf || 0]);
    }

    useEffect(() => {
        if (progress === null) {
            animNumber(0, value || 0, 500, setProgress);
        }
    }, [progress]);

    return (
        <div className={`task daily ${progress === outOf ? 'done' : ''}`}>
            <div className='daily-task-icon'>
                <img src={'/icons/32px/' + icon + '.png'} alt="daily" />
            </div>

            <div className='task-daily-content'>
                <p className="task-title daily">{getTitle()}</p>
                
                {description && <p className="task-description daily">{description}</p>}
                
                <div className='task-progress-bar'>
                    <div className='task-progress-bar-fill' style={{
                        width: `${progress}%`,
                    }}></div>
                </div>
            </div>
            {
                value === outOf ? (
                    <div className="task-checked-icon">
                        <img src="/icons/checked.png" alt="checked" />
                    </div>
                ) :
                <div className="daily-task-prize">
                    <p>{prize}$</p>
                </div>
            }
        </div>
    )
}

interface TaskJustTextProps {
    title: string;
    description: string;
}

export const TaskJustText = ({title, description}: TaskJustTextProps) => {
    return (
        <div className="task task-just-text">
            <p className="task-title task-just-text">{title}</p>
            <p className="task-description">{description}</p>
        </div>
    )
}
